import { hideProductInSelector, showProductInSelector, setVisibleProducts } from 'actions/productVisibilityActions';
import { fabric } from 'fabric';
import store from '../../store';
import localFontPreloader from '../../utils/localFontPreloader';

// Detect whether we're in buy plan mode based on buyplan info
let isBuyPlanMode = false;

// Function to detect buy plan mode from buyplan info
export function detectBuyPlanMode() {
  try {
    // Get buyplan info from Redux store
    const state = store.getState();
    const buyplanInfo = state?.get?.('buyplanInfoReducer')?.get?.('data');
    
    if (buyplanInfo) {
      // Check the state property to determine mode
      const buyplanState = buyplanInfo.get('state');
      
      isBuyPlanMode = buyplanState === 'buyplan';
      return isBuyPlanMode;
    }
    
    return false;
  } catch (e) {
    return false;
  }
}

// Expose mode detection to other components
export function isBuyPlan() {
  return isBuyPlanMode;
}

// Update mode on demand (can be called from other components)
export function updateBuyPlanMode() {
  detectBuyPlanMode();
  return isBuyPlanMode;
}

// Canonical Helpers.js - clean implementation
let fabcanvas = null;
let canvas_zoom_perc = 100;
const CUSTOM_PROPS = ['pimage', 'pname', 'skutxt', 'product_id', 'productcolorcode', 'productwprice', 'productrprice', 'productsizes', 'padding'];
let mainBuyplanItems = null;
let canvasBuyplanItems = null;
let selectorBuyplanItems = null;
let SEL_MONTH = null;
let ACTIVE_PAGE_ID = '';
let IS_EXPORT_PDF = false;
let FITCANVASVALUE = null;

// apply-to flags
let applyToSelector = true;
let applyToCanvas = true;

export function getApplyToSelector() { return applyToSelector; }
export function setApplyToSelector(v) { applyToSelector = !!v; }
export function getApplyToCanvas() { return applyToCanvas; }
export function setApplyToCanvas(v) { applyToCanvas = !!v; }

export function getSelMonth() { return SEL_MONTH; }
export function setSelMonth(m) { SEL_MONTH = m; }
export function getActivePageId() { return ACTIVE_PAGE_ID; }
export function setActivePageId(id) { ACTIVE_PAGE_ID = id; }
export function suspendAutoSave() { /* Auto save suspended */ }
export function resumeAutoSave() { /* Auto save resumed */ }
export function getIsExportPDF() { return IS_EXPORT_PDF; }
export function setIsExportPDF(v) { IS_EXPORT_PDF = !!v; }

// Helper function to create ID mappings based on the current mode
export function createIdMappings() {
  const inBuyPlanMode = detectBuyPlanMode();
  const idMap = new Map();
  
  if (!window.buyplanSummaryData) {
    return idMap;
  }
  
  try {
    const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
    if (buyplanItems) {
      for (const [, monthData] of buyplanItems.entries()) {
        const items = monthData.get('items');
        if (items) {
          items.forEach(item => {
            const buyplanItemId = String(item.get('id'));
            const productId = String(item.get('product_id'));
            
            // Always map buyplan item ID to product ID (this is always needed)
            idMap.set(buyplanItemId, productId);
            
            // Only map product ID to buyplan item ID in buy plan mode
            if (inBuyPlanMode) {
              idMap.set(productId, buyplanItemId);
            }
          });
        }
      }
    }
  } catch (e) {
    // Silently ignore mapping errors
  }
  
  return idMap;
}

export function getMainBuyplanItems() { return mainBuyplanItems; }
export function setMainBuyplanItems(items) { mainBuyplanItems = items; }
export function getCanvasBuyplanItems() { return canvasBuyplanItems; }
export function setCanvasBuyplanItems(items) { canvasBuyplanItems = items; }
export function getSelectorBuyplanItems() { return selectorBuyplanItems; }
export function setSelectorBuyplanItems(items) { selectorBuyplanItems = items; }
export function getCustomProps() { return CUSTOM_PROPS; }

export function getCanvas() { return fabcanvas; }
export function setCanvas(c) { if (c) fabcanvas = c; }

export function getLabelState() {
	try {
		const state = store.getState();
		const labelState = state && (state.get ? state.get('labelReducer') : state.labelReducer);
		return {
			product_name: labelState?.get ? labelState.get('product_name') : (labelState?.product_name || false),
			product_sku: labelState?.get ? labelState.get('product_sku') : (labelState?.product_sku || false),
			product_color: labelState?.get ? labelState.get('product_color') : (labelState?.product_color || false),
			product_wprice: labelState?.get ? labelState.get('product_wprice') : (labelState?.product_wprice || false),
			product_rprice: labelState?.get ? labelState.get('product_rprice') : (labelState?.product_rprice || false),
			product_sizes: labelState?.get ? labelState.get('product_sizes') : (labelState?.product_sizes || false),
		};
	} catch (e) {
		/* ignore */
		return { product_name: false, product_sku: false, product_color: false, product_wprice: false, product_rprice: false, product_sizes: false };
	}
}

export function addGroupedImage(pimage, options = {}, pname = '', skutxt = '', productcolorcode = '', productwprice = '', productrprice = '', productsizes = '', productIdOrOpacity = '', opacityArg = undefined) {
	const canvas = getCanvas();
	if (!canvas) return;
	const zoom = (canvas.getZoom && canvas.getZoom()) || 1;
	// normalize backward-compatible params: callers used to pass opacity as the 9th arg.
	let buyplanItemId = '';
	let opacity = 1;
	if (typeof opacityArg === 'undefined') {
		// Only 9th param provided (or none). If it's a small number <=1 treat it as opacity, else it's productId.
		if (typeof productIdOrOpacity === 'number' && productIdOrOpacity <= 1) {
			opacity = productIdOrOpacity;
			buyplanItemId = '';
		} else {
			buyplanItemId = productIdOrOpacity || '';
			opacity = 1;
		}
	} else {
		// Both provided: 9th is productId, 10th is opacity
		buyplanItemId = productIdOrOpacity || '';
		opacity = opacityArg;
	}

	// CRITICAL: Always ensure we have a valid product_id for canvas persistence
	let actualProductId = '';
	
	// Try to look up actual product_id from buyplan data
	if (buyplanItemId && window.buyplanSummaryData) {
		try {
			const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
			if (buyplanItems) {
				// Search through all months including SUGGESTED ITEMS
				for (const [, monthData] of buyplanItems.entries()) {
					const items = monthData.get('items');
					if (items) {
						const item = items.find(item => 
							item.get('id') === parseInt(buyplanItemId)
						);
						if (item) {
							actualProductId = item.get('product_id');
							break;
						}
					}
				}
			}
		} catch (e) {
			// If lookup fails, fall back to using buyplanItemId
			actualProductId = buyplanItemId;
		}
	}
	
	// CRITICAL: Always ensure we have a product_id - this prevents canvas objects from becoming invalid
	if (!actualProductId) {
		actualProductId = String(buyplanItemId || 'unknown');
	}

	fabric.Image.fromURL(pimage, function (img) {
		img.scaleToHeight(80);
		const labelState = getLabelState();
		let product_text = '';
		if (labelState.product_name) product_text = pname || '';
		if (labelState.product_sku) { if (product_text) product_text += '\n'; product_text += 'SKU: ' + (skutxt || ''); }
		if (labelState.product_color) { if (product_text) product_text += '\n'; product_text += productcolorcode || ''; }
		if (labelState.product_wprice) { if (product_text) product_text += '\n'; product_text += productwprice || ''; }
		if (labelState.product_rprice) { if (product_text) product_text += '\n'; product_text += productrprice || ''; }
		if (labelState.product_sizes) { if (product_text) product_text += '\n'; product_text += 'SIZE: ' + (productsizes || ''); }

		img.set({ originX: 'center', originY: 'top', left: 0, top: 0 });
		const imgScaledWidth = img.getScaledWidth();
		const imgScaledHeight = img.getScaledHeight();
		// Count the number of lines in the text to adjust height properly
		const lineCount = (product_text.match(/\n/g) || []).length + 1;
		
		// Calculate text height based on line count and font size
		// Use a more generous calculation to ensure all text fits
		const calculatedHeight = lineCount * 12; // 12px per line instead of 9px
		
		// Create text box with proper spacing for labels
		const producttext = new fabric.Textbox((product_text || '').toUpperCase(), {
			left: 0,
			top: imgScaledHeight,
			originX: 'center',
			originY: 'top',
			width: imgScaledWidth,
			height: calculatedHeight, // Height based on actual content
			fontSize: 7,
			fontFamily: 'Roboto',
			textAlign: 'center',
			fill: '#000000',
			fontWeight: 500,
			lineHeight: 1.2, // Proper line height for text
			padding: 2 // Small padding to prevent text cropping
		});

	// Calculate height simply as image + spacing + text - no extra padding
	const totalHeight = imgScaledHeight + 5 + producttext.height;
	
	// Position elements precisely
	img.set({
		top: 0,
		originY: 'top'
	});
	
	producttext.set({
		top: imgScaledHeight + 5,
		originY: 'top'
	});
	
	// Create simple group with fixed top position
	const group = new fabric.Group([img, producttext], {
		originX: 'center',
		originY: 'top',
		height: totalHeight, // Set exact height based on content
		strokeWidth: 1,
		padding: 3
	});
		if (options.left !== undefined) {
			group.left = options.left;
			group.top = options.top;
			group.setCoords();
			if (options.scaleX) { group.scaleX = options.scaleX; group.scaleY = options.scaleY; }
		} else if (canvas) {
			group.left = canvas.width / 2;
			group.top = canvas.height / 2;
			group.setCoords();
		}
	group.setControlsVisibility({ mt: false, mb: false, ml: false, mr: false });
	// attach custom properties used by save/restore and API payloads
	group.pimage = pimage;
	group.pname = pname;
	group.skutxt = buyplanItemId; // Use buyplan item ID for consistency with existing canvas objects
	group.product_id = actualProductId;
	group.productcolorcode = productcolorcode;
	group.productwprice = productwprice;
	group.productrprice = productrprice;
	group.productsizes = productsizes;
	group.opacity = opacity;
		canvas.add(group);
		group.scaleToWidth(100 * zoom);
		canvas.setActiveObject(group);
		canvas.renderAll();
		
		// Update productsEverOnCanvas before saving state
		if (buyplanItemId) {
			productsEverOnCanvas.add(String(buyplanItemId));
		}
		
		// Save state only after the image is fully loaded and added to canvas
		saveCanvasState();
	}, { crossOrigin: 'anonymous' });
}

export function addImage(pimage, options, pname, skutxt, productcolorcode, productwprice, productrprice, productsizes, productId, opacity) {
	// forward productId and optional opacity to addGroupedImage (backwards compatible)
	addGroupedImage(pimage, options, pname, skutxt, productcolorcode, productwprice, productrprice, productsizes, productId, opacity);
	
	// Note: saveCanvasState() is now called inside addGroupedImage after the image is fully loaded
	
	// Make sure we hide the product in the selector
	if (getApplyToSelector && getApplyToSelector()) {
		// Hide the primary ID (skutxt)
		store.dispatch(hideProductInSelector(skutxt));
		
		// CRITICAL: Always try to hide by both IDs (both buyplan item ID and product ID)
		// This ensures consistent behavior regardless of mode
		try {
		    const inBuyPlanMode = detectBuyPlanMode();
		    
		    // Create a direct lookup function to find product data
		    const findProductData = (idToFind) => {
		        if (!window.buyplanSummaryData) return null;
		        
		        try {
		            const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
		            if (!buyplanItems) return null;
		            
		            // Search through all months
		            for (const [, monthData] of buyplanItems.entries()) {
		                const items = monthData.get('items');
		                if (!items) continue;
		                
		                // Look for item by ID first (buyplan item ID)
		                let foundItem = items.find(item =>
		                    String(item.get('id')) === String(idToFind)
		                );
		                
		                // If not found by ID, try looking by product_id (in buy plan mode)
		                if (!foundItem && inBuyPlanMode) {
		                    foundItem = items.find(item =>
		                        String(item.get('product_id')) === String(idToFind)
		                    );
		                }
		                
		                if (foundItem) return foundItem;
		            }
		        } catch (e) {
		            return null;
		        }
		        
		        return null;
		    };
		    
		    // Find product data using skutxt (works in both modes)
		    const productData = findProductData(skutxt);
		    
		    if (productData) {
		        // Hide by buyplan item ID
		        const buyplanItemId = String(productData.get('id'));
		        if (buyplanItemId && buyplanItemId !== skutxt) {
		            store.dispatch(hideProductInSelector(buyplanItemId));
		        }
		        
		        // Hide by product ID (always do this regardless of mode)
		        const productId = String(productData.get('product_id'));
		        if (productId) {
		            store.dispatch(hideProductInSelector(productId));
		            
		            // Try to hide any DOM element directly
		            try {
		                const elem = document.getElementById(productId);
		                if (elem) elem.style.display = 'none';
		            } catch (e) {
		                // Ignore DOM manipulation errors
		            }
		        }
		    }
		} catch (e) {
		    // Ignore errors when hiding by product ID
		}
	}
	
	// Call API to refresh canvas state all products
	callCanvasStateAllAPI();
	
	// Force synchronize product visibility in buy plan mode
	// Delay execution slightly to ensure all state is updated
	setTimeout(() => {
		forceSyncProductVisibilityForBuyPlan();
	}, 500);
}

// Function to call canvas state all API
export function callCanvasStateAllAPI() {
	try {
		// API call removed to prevent main canvas re-renders
		// const reduxState = store.getState();
		// const buyplanId = reduxState.get('buyplanIdReducer')?.get('data')?.get('id');
		// const month = getSelMonth();
		// if (buyplanId && month) {
		// 	store.dispatch(getCanvasStateAll(buyplanId, month));
		// }
	} catch (e) {
		// Ignore errors
	}
}

// Special function specifically for buy plan mode to force visibility synchronization
export function forceSyncProductVisibilityForBuyPlan() {
	try {
		// Only run in buy plan mode
		const isBuyPlanMode = detectBuyPlanMode();

		if (!isBuyPlanMode) {
			return false;
		}
		
		// Get all canvas objects
		const canvas = getCanvas();
		if (!canvas) {
			return false;
		}
		
		// Create a complete mapping of all IDs
		const idMap = new Map();
		const productsOnCanvas = new Set();
		
		// Build ID mappings from buyplan summary data
		if (window.buyplanSummaryData) {
			try {
				const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
				if (buyplanItems) {
					
					// Get all months' products (including current month)
					for (const entry of buyplanItems.entries()) {
						const monthData = entry[1];
						
						const items = monthData.get('items');
						if (items && items.size > 0) {
							items.forEach(item => {
								const buyplanItemId = String(item.get('id'));
								const productId = String(item.get('product_id'));
								
								
								// Store bidirectional mappings
								idMap.set(buyplanItemId, productId);
								idMap.set(productId, buyplanItemId);
							});
						}
					}
				}
			} catch (e) {
				// Ignore mapping errors
			}
		}
		
		// Collect all product IDs from canvas objects (both skutxt and product_id)
		const objects = canvas.getObjects();
		objects.forEach(obj => {
			if (!obj) return;
			
			if (obj.skutxt) {
				const skutxt = String(obj.skutxt);
				productsOnCanvas.add(skutxt);
				
				// Add the mapped ID as well
				if (idMap.has(skutxt)) {
					const mappedId = idMap.get(skutxt);
					productsOnCanvas.add(mappedId);
				}
			}
			
			if (obj.product_id) {
				const productId = String(obj.product_id);
				productsOnCanvas.add(productId);
				
				// Add the mapped ID as well
				if (idMap.has(productId)) {
					const mappedId = idMap.get(productId);
					productsOnCanvas.add(mappedId);
				}
			}
		});
		
		// Hide products directly in DOM first (immediate feedback)
		
		productsOnCanvas.forEach(id => {
			try {
				// Direct DOM manipulation for immediate feedback
				document.querySelectorAll(`[id="${id}"]`).forEach(el => {
					el.style.display = 'none';
				});
				
				// Also try data attributes
				document.querySelectorAll(`[data-product-id="${id}"]`).forEach(el => {
					el.style.display = 'none';
				});
				
				document.querySelectorAll(`[data-sku="${id}"]`).forEach(el => {
					el.style.display = 'none';
				});
			} catch (e) {
				// Error hiding DOM element
			}
		});
		
		// Update Redux store state
		try {
			
			// Get all products from summary API
			const allProductIds = [];
			if (window.buyplanSummaryData) {
				const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
				if (buyplanItems) {
					for (const [, monthData] of buyplanItems.entries()) {
						const items = monthData.get('items');
						if (items && items.size > 0) {
							items.forEach(item => {
								const buyplanItemId = String(item.get('id'));
								const productId = String(item.get('product_id'));
								
								allProductIds.push(buyplanItemId);
								allProductIds.push(productId);
							});
						}
					}
				}
			}
			
			// Filter out products on canvas
			const uniqueProductIds = [...new Set(allProductIds)];
			const visibleProductIds = uniqueProductIds.filter(id => !productsOnCanvas.has(id));
			
			
			// Update Redux store in a single action
			store.dispatch(setVisibleProducts(visibleProductIds));
			
			// Force a ProductGroup component re-render
			triggerProductGroupRerender();
		} catch (e) {
			return e		
		}
		
		return true;
	} catch (e) {
		return false;
	}
}

// Global variable to force ProductGroup re-render
let productGroupRerenderTrigger = 0;

// Global variable to track products that have been on canvas (for undo/redo)
let productsEverOnCanvas = new Set();

// Flag to track when we're in an undo/redo operation
let isUndoRedoOperation = false;

// No longer needed - removed for cleaner code

// Function to trigger ProductGroup re-render (debounced to prevent excessive calls)
let rerenderTimeout = null;
export function triggerProductGroupRerender() {
	// Debounce the trigger to prevent excessive re-renders
	if (rerenderTimeout) {
		clearTimeout(rerenderTimeout);
	}
	
	rerenderTimeout = setTimeout(() => {
		productGroupRerenderTrigger++;
		rerenderTimeout = null;
	}, 150); // Increased from 50ms to 150ms for better stability
}

// Function to get the current trigger value
export function getProductGroupRerenderTrigger() {
	return productGroupRerenderTrigger;
}

export function updateSelectionCanvasItems(forceUpdate = false, forceKeepVisible = false) {
	if (!fabcanvas) return false;

	try {
		fabcanvas.discardActiveObject().renderAll();
		
		// Get all products on canvas
		const objects = fabcanvas.getObjects().filter(function (o) {
			return o.skutxt !== '';
		});

		// Count instances of each product on canvas (needed for selector visibility)
		const productCounts = {};
		objects.forEach(object => {
			if (object.skutxt) {
				const sku = String(object.skutxt);
				productCounts[sku] = (productCounts[sku] || 0) + 1;
				// Track products that have been on canvas
				productsEverOnCanvas.add(sku);
			}
		});
		
		// Check and remove products not matching the buyplan items
		const buyplanItems = getCanvasBuyplanItems();
		const productids = [];
		if (buyplanItems && getApplyToCanvas()) {
			// Normalize month strings so spaced and underscored variants compare equal
			const normalizeMonthForComparison = (monthStr) => {
				if (!monthStr) return '';
				return String(monthStr).toUpperCase().replace(/\s+/g, '_');
			};
			const selectedMonth = normalizeMonthForComparison(getSelMonth());
			for (let buyplanItem of buyplanItems.values()) {
				const shortMonth = buyplanItem.get("short_month");
				const normalizedShortMonth = normalizeMonthForComparison(shortMonth);
				if (normalizedShortMonth === selectedMonth) {
					buyplanItem.get("items").map((item) => {
						productids.push(String(item.get("id")));
					});
				}
			}
		}

		// Update visibility state for each object if "Apply To Canvas" is checked
		if (getApplyToCanvas()) {
			for (let i = 0; i < objects.length; i++) {
				const object = objects[i];
				if (object.skutxt) {
					// Don't hide products when we're on SUGGESTED ITEMS month - they should always be visible
					const currentMonth = getSelMonth();
					const isSuggestedItemsMonth = currentMonth && (
						String(currentMonth).toUpperCase() === 'SUGGESTED ITEMS' ||
						String(currentMonth).toUpperCase() === 'SUGGESTED_ITEMS'
					);
					
					// Check if this product should be visible based on filter criteria
					// Include forceKeepVisible to ensure compatibility with existing calls
					const shouldBeHiddenOnCanvas = !forceKeepVisible &&
						!isSuggestedItemsMonth &&
						!checkIFProductExists(productids, object.skutxt) &&
						getApplyToCanvas();

					// Update object visibility on canvas
					object.opacity = shouldBeHiddenOnCanvas ? 0 : 1;
					object.selectable = !shouldBeHiddenOnCanvas;
					object.visible = !shouldBeHiddenOnCanvas;
				}
			}
		}
		
		// Update Redux state for selector visibility
		// ONLY update if "Apply To Selector" is checked - no other conditions should affect selector
		if (getApplyToSelector()) {
			// Get all unique product SKUs that are currently on canvas
			const skusOnCanvas = Object.keys(productCounts);
			
			// Get ALL products that should be available in the selector
			const allSelectorProductIds = [];
			
			// When Apply to Selector is checked, use filtered data (getCanvasBuyplanItems)
			// Otherwise, we shouldn't need this logic, but keep it for safety using unfiltered data
			const buyplanItems = getCanvasBuyplanItems();
			if (buyplanItems) {
				for (let buyplanItem of buyplanItems.values()) {
					buyplanItem.get("items").map((item) => {
						allSelectorProductIds.push(String(item.get("id")));
					});
				}
			}
			
			// Also include products that were previously on canvas
			const allProductsToConsider = [...new Set([...allSelectorProductIds, ...productsEverOnCanvas])];
			
			// Create a mapping between product IDs and buyplan item IDs
			const idMap = new Map();
			const inBuyPlanMode = detectBuyPlanMode();
			
			if (window.buyplanSummaryData) {
			  try {
			    const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
			    if (buyplanItems) {
			      for (let buyplanItem of buyplanItems.values()) {
			        buyplanItem.get("items").forEach((item) => {
			          const buyplanItemId = String(item.get("id"));
			          const productId = String(item.get("product_id"));
			          
			          // Always store the mapping in the forward direction
			          idMap.set(buyplanItemId, productId);
			          
			          // Only create reverse mapping in buy plan mode
			          if (inBuyPlanMode) {
			            idMap.set(productId, buyplanItemId);
			          }
			        });
			      }
			    }
			  } catch (e) {
			    // Ignore mapping errors
			  }
			}
			
			// Enhanced canvas SKUs set that includes both ID types
			const enhancedSkusOnCanvas = new Set();
			
			// Add all primary SKUs first
			skusOnCanvas.forEach(sku => {
			  enhancedSkusOnCanvas.add(sku);
			});
			
			// Add all mapped SKUs (in both directions)
			skusOnCanvas.forEach(sku => {
			  // Forward mapping
			  if (idMap.has(sku)) {
			    enhancedSkusOnCanvas.add(idMap.get(sku));
			  }
			  
			  // Reverse mapping
			  for (const [key, value] of idMap.entries()) {
			    if (value === sku) {
			      enhancedSkusOnCanvas.add(key);
			    }
			  }
			});
			
			// Batch dispatch product visibility updates - ONLY if "Apply To Selector" is checked
			if (getApplyToSelector()) {
				const visibleProducts = allProductsToConsider.filter(id => !enhancedSkusOnCanvas.has(id));
				store.dispatch(setVisibleProducts(visibleProducts));
			}
			
			// For initial loads or forced updates, do a more aggressive update with DOM
			// ONLY if "Apply To Selector" is checked
			if (forceUpdate && getApplyToSelector()) {
				skusOnCanvas.forEach(sku => {
					try {
						const elem = document.getElementById(sku);
						if (elem) elem.style.display = 'none';
						
						if (idMap.has(sku)) {
							const mappedId = idMap.get(sku);
							const mappedElem = document.getElementById(mappedId);
							if (mappedElem) mappedElem.style.display = 'none';
						}
						
						for (const [key, value] of idMap.entries()) {
							if (value === sku) {
								const reverseElem = document.getElementById(key);
								if (reverseElem) reverseElem.style.display = 'none';
							}
						}
					} catch (e) {
						// Silently fail if DOM manipulation fails
					}
				});
			}
		}

		fabcanvas.discardActiveObject().renderAll();
		
		// Trigger a re-render after a delay
		if (forceUpdate) {
			triggerProductGroupRerender();
		}
		return true;
	} catch (error) {
		return false;
	}
}

// Fix the inverted logic - this function should return true if the product exists in the array
function checkIFProductExists(productids, sku) { return productids.indexOf(String(sku)) !== -1; }

// We no longer need global flags to track visibility

export function udpateGroupLabels() {
	const canvas = getCanvas();
	if (!canvas) return false;
	
	// debugger
	try {
		// Get all product objects on canvas
		const objects = (canvas.getObjects && canvas.getObjects()) || [];
		
		// Current label settings
		const labelState = getLabelState();
		
		// Update each object's text content based on current label settings
		for (let i = 0; i < objects.length; i++) {
			const obj = objects[i];
			if (!obj || !obj.skutxt || !obj._objects || obj._objects.length < 2) continue;
			
			// Find the image and text objects in the group
			let imageObject = null;
			let textObject = null;
			
			// The image is usually the first object (index 0) and text is second (index 1)
			// But we'll check types to be sure
			for (let j = 0; j < obj._objects.length; j++) {
				const subObj = obj._objects[j];
				if (!subObj) continue;
				
				if (subObj.type === 'image') {
					imageObject = subObj;
				} else if (subObj.type === 'textbox') {
					textObject = subObj;
				}
			}
			
			// Skip if we don't have both image and text
			if (!imageObject || !textObject) continue;
			
			// Build new text content based on current label settings
			let product_text = '';
			if (labelState.product_name) product_text = obj.pname || '';
			if (labelState.product_sku) { if (product_text) product_text += '\n'; product_text += 'SKU: ' + (obj.skutxt || ''); }
			if (labelState.product_color) { if (product_text) product_text += '\n'; product_text += obj.productcolorcode || ''; }
			if (labelState.product_wprice) { if (product_text) product_text += '\n'; product_text += obj.productwprice || ''; }
			if (labelState.product_rprice) { if (product_text) product_text += '\n'; product_text += obj.productrprice || ''; }
			if (labelState.product_sizes) { if (product_text) product_text += '\n'; product_text += 'SIZE: ' + (obj.productsizes || ''); }
			
			// First cache the original position of the text relative to the image
			const imgScaledHeight = imageObject.getScaledHeight();
			
			// Update only the text content - minimal changes
			textObject.set({
				'text': (product_text || '').toUpperCase(),
				'lineHeight': 1.2,
				'dirty': true
			});
			
			// // Force the text to maintain its exact position - critical fix
			textObject.set({
				'top':  imgScaledHeight - 48,
				'left': 0,
				'originX': 'center',
				'originY': 'top'
			});
			
			// // Calculate total height without any extra padding
			const totalHeight = imgScaledHeight + 35 + textObject.height;
			
			// Set the group height to exactly match the content - no extra padding
			obj.set({
				'height': totalHeight,
				'padding': 3,
				'dirty': true
			});
			
			// Force immediate rendering to apply changes
			canvas.renderAll();
		}
		
		// Render the canvas to show the updated text
		canvas.renderAll();
		
		// Save the canvas state with the updated text
		saveCanvasState();
		
		return true;
	} catch (e) {
		return false;
	}
}

export function addText() { 
  const canvas = getCanvas(); 
  if (!canvas) return; 
  const text = new fabric.Textbox('Add Heading', { 
    fontSize: 12, 
    width: 50, 
    fill: '#000000', 
    fontFamily: 'Roboto', 
    fontWeight: 500 
  }); 
  canvas.add(text); 
  text.center(); 
  canvas.setActiveObject(text); 
  canvas.renderAll(); 
  saveCanvasState(); 
}
export function addRect() { 
  const canvas = getCanvas(); 
  if (!canvas) return; 
  const rect = new fabric.Rect({ 
    left: 100, 
    top: 100, 
    fill: 'transparent', 
    stroke: '#000000', 
    strokeWidth: 1, 
    strokeUniform: true, 
    width: 200, 
    height: 200 
  }); 
  canvas.add(rect); 
  rect.center(); 
  rect.sendToBack(); 
  canvas.setActiveObject(rect); 
  canvas.renderAll(); 
  saveCanvasState(); 
}

function setCanvasWidthHeight(width, height) { const canvas = getCanvas(); if (!canvas) return; canvas.setDimensions({ width, height }); canvas.calcOffset && canvas.calcOffset(); canvas.renderAll(); }

export function resetCanvasZoom(zPerc) { const canvas = getCanvas(); zPerc = parseInt(zPerc); canvas_zoom_perc = zPerc; if (!canvas) return; if (canvas_zoom_perc <= 100) { setCanvasWidthHeight(canvas.width, canvas.height); canvas.setZoom(canvas_zoom_perc / 100); } else { setCanvasWidthHeight(canvas.width * ((canvas_zoom_perc / 100) / canvas.getZoom()), canvas.height * ((canvas_zoom_perc / 100) / canvas.getZoom())); canvas.setZoom(canvas_zoom_perc / 100); } canvas.discardActiveObject && canvas.discardActiveObject(); canvas.requestRenderAll && canvas.requestRenderAll(); saveCanvasState(); }

export function fittocanvas() {
	const canvas = getCanvas();
	if (!canvas) return false;
	const canvasArea = document.querySelector('.canvasArea');
	if (!canvasArea) return false;
	try {
		if (!canvas.getObjects().length) { canvas_zoom_perc = 100; canvas.setZoom(1); setFittoCanvas(100); canvas.requestRenderAll(); saveCanvasState(); return true; }
		const viewportWidth = canvasArea.clientWidth; const viewportHeight = canvasArea.clientHeight;
		const sel = new fabric.ActiveSelection(canvas.getObjects(), { canvas });
		canvas.setActiveObject(sel);
		sel.left = sel.top = 5; sel.setCoords(); canvas.requestRenderAll();
		const wPerc = (viewportWidth / (sel.width * sel.scaleX));
		const hPerc = (viewportHeight / (sel.height * sel.scaleY));
		let zPerc = Math.min(wPerc, hPerc) * 100; zPerc = Math.max(10, zPerc * 0.9);
		if (isNaN(zPerc) || !isFinite(zPerc)) zPerc = 100;
		canvas_zoom_perc = zPerc; canvas.setZoom(canvas_zoom_perc / 100); setFittoCanvas(canvas_zoom_perc); canvas.discardActiveObject && canvas.discardActiveObject(); canvas.requestRenderAll && canvas.requestRenderAll(); saveCanvasState(); return true;
	} catch (e) { /* ignore */ }
}

export function getFittoCanvas() { return FITCANVASVALUE; }
export function setFittoCanvas(value) { FITCANVASVALUE = value; }

export function handleZoomCanvas(value) { const canvas = getCanvas(); canvas_zoom_perc = value; if (!canvas) return; if (canvas_zoom_perc <= 100) { setCanvasWidthHeight(canvas.width, canvas.height); canvas.setZoom(canvas_zoom_perc / 100); } else { setCanvasWidthHeight(canvas.width * ((canvas_zoom_perc / 100) / canvas.getZoom()), canvas.height * ((canvas_zoom_perc / 100) / canvas.getZoom())); canvas.setZoom(canvas_zoom_perc / 100); } canvas.discardActiveObject && canvas.discardActiveObject(); canvas.requestRenderAll && canvas.requestRenderAll(); saveCanvasState(); }

export function handleZoomChange(event) { const canvas = getCanvas(); const zValue = event && event.target && event.target.value; if (zValue === 'FitToCanvas') { fittocanvas(); return; } canvas_zoom_perc = parseInt(zValue); if (!canvas) return; canvas.setZoom(canvas_zoom_perc / 100); canvas.discardActiveObject && canvas.discardActiveObject(); canvas.requestRenderAll && canvas.requestRenderAll(); saveCanvasState(); }

export function saveCanvasState() {
	if (!fabcanvas || !fabcanvas.stateaction) return;
	
	// Initialize state if it doesn't exist
	if (!fabcanvas.state) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
	}

	// Before serializing, make sure all products are visible
	// This ensures that products don't disappear when saving after label changes
	const objects = fabcanvas.getObjects();

	// No need to set flags - we'll directly ensure objects are visible here
	objects.forEach((obj) => {
		if (obj && obj.skutxt) {
			// Always force products to be visible in saved state
			obj.opacity = 1;
			obj.selectable = true;
			obj.visible = true;
		}
	});
	
	const statejson = JSON.stringify(fabcanvas.toJSON(CUSTOM_PROPS));
	
	// Check if this state is different from the last saved state
	if (fabcanvas.state.length > 0) {
		const laststatejson = fabcanvas.state[fabcanvas.state.length - 1];
		if (laststatejson === statejson) {
			return false; // No change, don't save
		}
	}
	
	// If we're not at the end of the state array (i.e., we've undone some actions),
	// remove all states after the current index before adding the new one
	if (fabcanvas.index < fabcanvas.state.length - 1) {
		fabcanvas.state = fabcanvas.state.slice(0, fabcanvas.index + 1);
	}
	
	// Add the new state
	fabcanvas.state.push(statejson);
	fabcanvas.index = fabcanvas.state.length - 1;
	
	// Store productsEverOnCanvas in the canvas state for persistence across undo/redo
	// Convert Set to Array for proper serialization
	// Only update if we're not in the middle of an undo/redo operation
	if (!isUndoRedoOperation) {
		fabcanvas.productsEverOnCanvas = Array.from(productsEverOnCanvas);
	}

	// Limit the number of saved states to prevent memory issues
	if (fabcanvas.state.length > 50) {
		fabcanvas.state = fabcanvas.state.slice(-50);
		fabcanvas.index = fabcanvas.state.length - 1;
	}
	
	// Trigger a rerender of the selector canvas when state changes
	// Skip during undo/redo operations as they handle this separately
	if (!isUndoRedoOperation) {
		triggerProductGroupRerender();
	}
	
	// Defensive: ensure product_id is populated correctly when missing
	try {
		const objsForSave = (fabcanvas.getObjects && fabcanvas.getObjects()) || [];
		for (let i = 0; i < objsForSave.length; i++) {
			const obj = objsForSave[i];
			if (!obj) continue;
			
			// CRITICAL: Always ensure product_id is set for canvas persistence
			// This prevents products from disappearing on save
			if (!obj.product_id || obj.product_id === '' || obj.product_id === null) {
				let actualProductId = '';
				
				// Try to look up actual product_id from buyplan data
				if (obj.skutxt && window.buyplanSummaryData) {
					try {
						const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
						if (buyplanItems) {
							// Search through all months including SUGGESTED ITEMS
							for (const [, monthData] of buyplanItems.entries()) {
								const items = monthData.get('items');
								if (items) {
									const item = items.find(item => 
										item.get('id') === parseInt(obj.skutxt)
									);
									if (item) {
										actualProductId = item.get('product_id');
										break;
									}
								}
							}
						}
					} catch (e) {
						// If lookup fails, fall back to using skutxt
						actualProductId = String(obj.skutxt);
					}
				}
				
				// CRITICAL: Always ensure we have a product_id - this prevents canvas objects from becoming invalid
				if (!actualProductId) {
					actualProductId = String(obj.skutxt || obj.id || 'unknown');
				}
				
				obj.product_id = actualProductId;
			}
		}
	} catch (e) { /* ignore */ }
}

export function undo() {
	if (!fabcanvas) return;
	
	// Initialize state if it doesn't exist
	if (!fabcanvas.state) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
		return;
	}
	
	fabcanvas.stateaction = false;
	let index = fabcanvas.index || 0;
	const state = fabcanvas.state || [];
	
	// Allow undo if we have at least 1 state and we're not already at the first state
	// OR if we have only 1 state (clear the canvas completely)
	if (index > 0 || (index === 0 && state.length === 1)) {
		if (index > 0) {
			index -= 1;
		} else {
			// If we're at index 0 with only 1 state, go to empty canvas
			index = -1;
		}
		
		// Set flag to indicate we're in an undo operation
		isUndoRedoOperation = true;
		
		fabcanvas.clear();
		
		if (index >= 0 && state[index]) {
			// Wait for fonts to be ready before loading canvas state
			localFontPreloader.waitForFontsReady().then(() => {
				// Load the previous state
				fabcanvas.loadFromJSON(state[index], function () {
					fabcanvas.renderAll();
					fabcanvas.stateaction = true;
					fabcanvas.index = index;
					
					// Restore productsEverOnCanvas from canvas state
					if (fabcanvas.productsEverOnCanvas && Array.isArray(fabcanvas.productsEverOnCanvas)) {
						productsEverOnCanvas = new Set(fabcanvas.productsEverOnCanvas);
					}

					// Add small delay to ensure canvas objects are fully loaded before updating visibility
					setTimeout(() => {
						// Update selector visibility after undo
						updateSelectionCanvasItems();
						
						// Force re-render of selector canvas after Redux actions are processed
						setTimeout(() => {
							triggerProductGroupRerender();
							// Clear the undo/redo flag after operations are complete
							isUndoRedoOperation = false;
						}, 50);
					}, 100); // Give canvas time to fully load objects
				});
			});
		} else {
			// Empty canvas (no state to load)
			fabcanvas.renderAll();
			fabcanvas.stateaction = true;
			fabcanvas.index = index;
			
			// Update selector visibility after undo
			updateSelectionCanvasItems();
			
			// Force re-render of selector canvas after Redux actions are processed
			setTimeout(() => {
				triggerProductGroupRerender();
			}, 50);
		}
	} else {
		fabcanvas.stateaction = true;
	}
}

export function redo() {
	if (!fabcanvas) return;
	
	// Initialize state if it doesn't exist
	if (!fabcanvas.state) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
		return;
	}
	
	fabcanvas.stateaction = false;
	let index = fabcanvas.index || 0;
	const state = fabcanvas.state || [];
	
	// Allow redo if we're not at the last state
	// Handle case when index is -1 (empty canvas) and we have states to redo to
	if (index < state.length - 1 || index === -1) {
		if (index === -1) {
			// If we're at empty canvas, go to first state (index 0)
			index = 0;
		} else {
			index += 1;
		}
		
		// Set flag to indicate we're in a redo operation
		isUndoRedoOperation = true;
		
		fabcanvas.clear();
		// Wait for fonts to be ready before loading canvas state
		localFontPreloader.waitForFontsReady().then(() => {
			fabcanvas.loadFromJSON(state[index], function () {
				fabcanvas.renderAll();
				fabcanvas.stateaction = true;
				fabcanvas.index = index;
				
				// Restore productsEverOnCanvas from canvas state
				if (fabcanvas.productsEverOnCanvas && Array.isArray(fabcanvas.productsEverOnCanvas)) {
					productsEverOnCanvas = new Set(fabcanvas.productsEverOnCanvas);
				}
				
				// Add small delay to ensure canvas objects are fully loaded before updating visibility
				setTimeout(() => {
					// Update selector visibility after redo
					updateSelectionCanvasItems();
					
					// Force re-render of selector canvas after Redux actions are processed
					setTimeout(() => {
						triggerProductGroupRerender();
						// Clear the undo/redo flag after operations are complete
						isUndoRedoOperation = false;
					}, 50);
				}, 100); // Give canvas time to fully load objects
			});
		});
	} else {
		fabcanvas.stateaction = true;
	}
}

export function resetState() {
	if (fabcanvas) {
		fabcanvas.state = [];
		fabcanvas.index = -1;
		fabcanvas.stateaction = true;
		
		// Clear the products ever on canvas tracking
		productsEverOnCanvas.clear();
		
		// Save the initial empty state so undo/redo works properly
		const initialState = JSON.stringify(fabcanvas.toJSON(CUSTOM_PROPS));
		fabcanvas.state.push(initialState);
		fabcanvas.index = 0;
	}
}

export function loadPageState(pageId, canvasState) {
	if (!fabcanvas) return false;
	setActivePageId(pageId);
	const productsOnCanvas = new Set();
	if (fabcanvas.getObjects) {
		const objects = fabcanvas.getObjects().filter(o => o.skutxt);
		objects.forEach(obj => { if (obj.skutxt) productsOnCanvas.add(obj.skutxt); });
	}
	fabcanvas.clear();
	resetState();
	if (canvasState) {
		// Wait for fonts to be ready before loading canvas state
		localFontPreloader.waitForFontsReady().then(() => {
			fabcanvas.loadFromJSON(canvasState, function () {
				fabcanvas.renderAll();
				const newObjects = fabcanvas.getObjects().filter(o => o.skutxt);
				const productsOnNewPage = new Set();
				newObjects.forEach(obj => {
					if (obj.skutxt) {
						productsOnNewPage.add(obj.skutxt);
						if (getApplyToSelector && getApplyToSelector()) store.dispatch(hideProductInSelector(obj.skutxt));
					}
				});
				// Create ID mappings with awareness of current mode
				// Check the mode explicitly
				const inBuyPlanMode = detectBuyPlanMode();
				
				const idMap = new Map();
				if (window.buyplanSummaryData) {
					try {
						const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
						if (buyplanItems) {
							for (let buyplanItem of buyplanItems.values()) {
								buyplanItem.get("items").forEach((item) => {
									const buyplanItemId = String(item.get("id"));
									const productId = String(item.get("product_id"));
									
									// Always store mapping in the forward direction
									idMap.set(buyplanItemId, productId);
									
									// Only create reverse mapping in buy plan mode
									if (inBuyPlanMode) {
										idMap.set(productId, buyplanItemId);
									}
								});
							}
						}
					} catch (e) {
						// Ignore mapping errors
					}
				}
				
				productsOnCanvas.forEach(skutxt => {
					if (!productsOnNewPage.has(skutxt)) {
						// Show the primary ID
						if (getApplyToSelector && getApplyToSelector()) {
							store.dispatch(showProductInSelector(skutxt));
						}
						
						// Also show the alternate ID if mapping exists - no mode check needed
						if (idMap.has(skutxt) && getApplyToSelector && getApplyToSelector()) {
							store.dispatch(showProductInSelector(idMap.get(skutxt)));
						}
					}
				});
				updateSelectionCanvasItems();
				// Force re-render of selector canvas after visibility is updated
				triggerProductGroupRerender();
			});
		});
	} else {
		productsOnCanvas.forEach(skutxt => { if (getApplyToSelector && getApplyToSelector()) store.dispatch(showProductInSelector(skutxt)); });
		updateSelectionCanvasItems();
		// Force re-render of selector canvas after visibility is updated
		triggerProductGroupRerender();
	}
	
	// CRITICAL FIX: After loading page state, we need to hide products that are on ANY page's canvas
	// This ensures that when navigating between pages, products already on canvas are properly hidden
	if (getApplyToSelector && getApplyToSelector()) {
		// Create ID mappings with explicit mode awareness
		// Always check the mode explicitly
		const inBuyPlanMode = detectBuyPlanMode();
		
		const idMap = new Map();
		if (window.buyplanSummaryData) {
			try {
				const buyplanItems = window.buyplanSummaryData.get('buyplan_items');
				if (buyplanItems) {
					for (let buyplanItem of buyplanItems.values()) {
						buyplanItem.get("items").forEach((item) => {
							const buyplanItemId = String(item.get("id"));
							const productId = String(item.get("product_id"));
							
							// Always map buyplan item ID to product ID
							idMap.set(buyplanItemId, productId);
							
							// Only map product ID to buyplan item ID in buy plan mode
							if (inBuyPlanMode) {
								idMap.set(productId, buyplanItemId);
							}
						});
					}
				}
			} catch (e) {
				// Ignore mapping errors
			}
		}
		
		// Get all products that are on any page's canvas from the canvas state all data
		const canvasStateAll = store.getState().get("canvasInfoReducer")?.getIn(["canvasStateAll", "data"]);
		if (canvasStateAll && canvasStateAll.get) {
			try {
				const canvasData = canvasStateAll.get("data");
				if (canvasData && canvasData.size > 0) {
					const monthData = canvasData.find(item => item.get("month") === getSelMonth());
					if (monthData) {
						const allProductsOnCanvas = monthData.get("products")?.toJS() || [];
						// Hide all products that are on any page's canvas
						allProductsOnCanvas.forEach(product => {
							if (product.id) {
								const productId = String(product.id);
								// Hide the primary ID
								store.dispatch(hideProductInSelector(productId));
								
								// Also hide the alternate ID if mapping exists - no mode check needed
								if (idMap.has(productId)) {
									store.dispatch(hideProductInSelector(idMap.get(productId)));
								}
							}
						});
					}
				}
			} catch (e) {
				// If there's an error accessing canvas state all, fall back to current behavior
			}
		}
	}
}

// Mark the canvas as saved (used by FabricCanvas after load/save)
export function markCanvasSaved() {
	try {
		if (!fabcanvas) return false;

		// Before marking as saved, ensure all products have correct visibility
		const objects = fabcanvas.getObjects();

		// We'll mark all objects as visible regardless of label changing state
		objects.forEach((obj) => {
			if (obj && obj.skutxt) {
				// Force all products to be visible when saving
				obj.opacity = 1;
				obj.selectable = true;
				obj.visible = true;

				// Mark all product objects as force visible
				obj.__forceVisible = true;
			}
		});

		fabcanvas.modified = false;

		// Trigger a selector canvas update after save is complete
		// This ensures products remain properly synced between canvas and selector
		setTimeout(() => {
			updateSelectionCanvasItems(true); // Force visibility
			// Force a render to ensure visibility changes are applied
			if (fabcanvas && typeof fabcanvas.renderAll === 'function') {
				fabcanvas.renderAll();
			}

			// Schedule another update to ensure visibility persists
			setTimeout(() => {
				updateSelectionCanvasItems(true);
				if (fabcanvas && typeof fabcanvas.renderAll === 'function') {
					fabcanvas.renderAll();
				}
			}, 1000);
		}, 200);

		return true;
	} catch (e) {
		return false;
	}
}

// Returns true if the main canvas has unsaved modifications
export function isMainCanvasModified() {
	try {
		if (!fabcanvas) return false;
		return !!fabcanvas.modified;
	} catch (e) {
		return false;
	}
}

// Initialize canvas event handlers (safe no-op if canvas not ready)
export function initCanvasEvents(onToolbarUpdate) {
	try {
		const canvas = getCanvas();
		if (!canvas) return;

		// ensure we don't attach duplicates
		if (canvas._helpersEventsAttached) return;
		canvas._helpersEventsAttached = true;

		const markModified = () => {
			try {
				canvas.modified = true;
				// Don't call onToolbarUpdate with empty object as it resets toolbar state
				// The toolbar state should be preserved during modifications
			} catch (e) {
				/* ignore */
			}
		};

		// Handle selection events to show/hide text size control
		const handleSelection = () => {
			try {
				const activeObject = canvas.getActiveObject();
				const isText = activeObject && (
					activeObject.type === "textbox" ||
					activeObject.type === "i-text" ||
					activeObject.type === "text" ||
					(activeObject.isType && activeObject.isType("textbox"))
				);
				
				if (typeof onToolbarUpdate === 'function') {
					onToolbarUpdate({ 
						showFontSizeBtn: isText,
						fontSize: isText ? (activeObject.fontSize || 12) : null
					});
				}
			} catch (e) {
				/* ignore */
			}
		};

		const handleDeselection = () => {
			try {
				if (typeof onToolbarUpdate === 'function') {
					onToolbarUpdate({ 
						showFontSizeBtn: false,
						fontSize: null
					});
				}
			} catch (e) {
				/* ignore */
			}
		};

		canvas.on && canvas.on('object:modified', markModified);
		canvas.on && canvas.on('object:added', markModified);
		canvas.on && canvas.on('object:removed', markModified);
		canvas.on && canvas.on('object:scaled', markModified);
		canvas.on && canvas.on('object:moving', markModified);
		
		// Add selection event handlers
		canvas.on && canvas.on('selection:created', handleSelection);
		canvas.on && canvas.on('selection:updated', handleSelection);
		canvas.on && canvas.on('selection:cleared', handleDeselection);
		
		// Initialize state - ensure text size control is hidden on startup
		handleDeselection();
	} catch (e) {
		/* ignore */
	}
}

// Utility: validate a date string (used by BuyplanSummaryContainer)
export function isValidDate(d) {
	try {
		if (!d) return false;
		const dt = new Date(d);
		return !isNaN(dt.getTime());
	} catch (e) {
		return false;
	}
}

// Utility to calculate fitting dimensions for an object in a page area
export function calculateXYWidthHeight(objWidth, objHeight, maxWidth, maxHeight) {
	try {
		const wRatio = maxWidth / objWidth;
		const hRatio = maxHeight / objHeight;
		const ratio = Math.min(wRatio, hRatio, 1);
		return { width: Math.round(objWidth * ratio), height: Math.round(objHeight * ratio), ratio };
	} catch (e) {
		return { width: objWidth, height: objHeight, ratio: 1 };
	}
}

// Add 'Powered by' footer with optional logo to a jsPDF instance
export function addPoweredByText(pdf, xMargin, pageWidth, pageHeight, logoBaseImg) {
	try {
		if (!pdf) return;
		const text = 'Powered by Brandscope';
		const fontSize = 10;
		pdf.setFontSize(fontSize);
		pdf.setTextColor(115, 115, 115);
		const textWidth = pdf.getTextWidth(text || '');
		const x = pageWidth - xMargin - textWidth - 10;
		const y = pageHeight - xMargin / 2 - 15; // Increased spacing from bottom
		
		if (logoBaseImg) {
			try {
				const logoW = 20;
				const logoH = 20;
				// Position logo inline with text baseline - move logo up to align with text
				pdf.addImage(logoBaseImg, 'PNG', x - logoW - 6, y - logoH + 6, logoW, logoH);
			} catch (e) {
				// ignore logo add failures
			}
		}
		pdf.text(text, x, y);
	} catch (e) {
		// ignore
	}
}

// Add month title area on the page header (right aligned)
export function addMonthTitle(pdf, pageWidth, xMargin, yMargin, monthTitle) {
	try {
		if (!pdf) return;
		const title = monthTitle || '';
		const fontSize = 12;
		pdf.setFontSize(fontSize);
		pdf.setTextColor(115, 115, 115);
		const w = pdf.getTextWidth(title || '');
		const x = pageWidth - xMargin - w;
		const y = yMargin + 12;
		if (title) pdf.text(title, x, y);
	} catch (e) {
		// ignore
	}
}

// Add page number text at bottom-right
export function addPageNumber(pdf, xMargin, pageWidth, pageHeight, text) {
	try {
		if (!pdf) return;
		const txt = text || '';
		const fontSize = 9;
		pdf.setFontSize(fontSize);
		pdf.setTextColor(115, 115, 115);
		const w = pdf.getTextWidth(txt || '');
		const x = pageWidth - xMargin - w;
		const y = pageHeight - xMargin / 2;
		if (txt) pdf.text(txt, x, y);
	} catch (e) {
		// ignore
	}
}

// Set an active style on the currently selected object(s)
export function setActiveStyle(key, value) {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		const active = canvas.getActiveObject && canvas.getActiveObject();
		if (!active) return;
		try {
			active.set(key, value);
			if (active.setCoords) active.setCoords();
			if (canvas.requestRenderAll) canvas.requestRenderAll();
			saveCanvasState();
		} catch (e) {
			// ignore set failures
		}
	} catch (e) {
		// ignore
	}
}

// Add simple vertical/horizontal lines and a line (arrow placeholder) to the canvas
export function addVLine() {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		const line = new fabric.Line([0, -100, 0, 100], {
			left: canvas.width / 2,
			top: canvas.height / 2,
			stroke: '#000000',
			strokeWidth: 1,
			selectable: true,
		});
		canvas.add(line);
		canvas.setActiveObject(line);
		canvas.requestRenderAll && canvas.requestRenderAll();
		saveCanvasState();
	} catch (e) {
		// ignore
	}
}

export function addHLine() {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		const line = new fabric.Line([-100, 0, 100, 0], {
			left: canvas.width / 2,
			top: canvas.height / 2,
			stroke: '#000000',
			strokeWidth: 1,
			selectable: true,
		});
		canvas.add(line);
		canvas.setActiveObject(line);
		canvas.requestRenderAll && canvas.requestRenderAll();
		saveCanvasState();
	} catch (e) {
		// ignore
	}
}

export function addLineArrow() {
	try {
		const canvas = getCanvas();
		if (!canvas) return;
		
		// Create a horizontal line
		const line = new fabric.Line([0, 0, 95, 0], {
			stroke: '#000000',
			strokeWidth: 3,
			originX: 'left',
			originY: 'center'
		});

		// Create arrowhead using a polygon for better control
		const arrowhead = new fabric.Polygon([
			{x: 120, y: 0},    // tip of arrow (extended further right)
			{x: 95, y: -10},   // top back point (wider base)
			{x: 95, y: 10}     // bottom back point (wider base)
		], {
			fill: '#000000',
			originX: 'left',
			originY: 'center'
		});

		// Group the line and arrowhead together
		const linearrowObj = new fabric.Group([line, arrowhead], {
			originX: 'center',
			originY: 'center',
			padding: 20
		});

		// Position the group in the center of the canvas
		linearrowObj.left = canvas.width / 2;
		linearrowObj.top = canvas.height / 2;
		linearrowObj.setCoords();

		canvas.add(linearrowObj);
		canvas.setActiveObject(linearrowObj);
		canvas.requestRenderAll && canvas.requestRenderAll();
		saveCanvasState();
	} catch (e) {
		// ignore
	}
}
